import React from 'react';
import { WebsiteNavbar } from './WebsiteNavbar';
import { WebsiteFooter } from './WebsiteFooter';
import { 
  Calendar, 
  Users, 
  Building2, 
  FileText, 
  MessageSquare, 
  Shield,
  Vote,
  Video,
  BarChart3,
  Bell,
  FileCheck,
  Wrench,
  Check,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const WebsiteApplication = () => {
  const mainFeatures = [
    {
      icon: Calendar,
      title: 'Assemblées Générales',
      description: 'Organisez vos AG de A à Z : convocations, ordres du jour, résolutions, votes et procès-verbaux automatisés.',
      details: [
        'Vote en présentiel ou à distance',
        'Visioconférence intégrée',
        'Génération automatique des PV',
        'Gestion des pouvoirs et délégations'
      ],
      color: 'emerald'
    },
    {
      icon: Vote,
      title: 'Vote par Correspondance',
      description: 'Permettez à vos coproprietaires de voter avant l\'AG grâce à notre système VPC innovant.',
      details: [
        'Bulletins de vote dématérialisés',
        'Reconnaissance OCR par IA',
        'Comptabilisation automatique',
        'Traçabilité complète'
      ],
      color: 'blue'
    },
    {
      icon: Video,
      title: 'Visioconférence',
      description: 'Intégration Daily.co pour des AG hybrides fluides et professionnelles.',
      details: [
        'Qualité HD garantie',
        'Partage d\'écran',
        'Chat intégré',
        'Enregistrement optionnel'
      ],
      color: 'purple'
    },
    {
      icon: Building2,
      title: 'Gestion des Immeubles',
      description: 'Centralisez toutes les informations de vos copropriétés dans une interface intuitive.',
      details: [
        'Fiche immeuble complète',
        'Gestion des lots et tantièmes',
        'Documents techniques',
        'Historique des interventions'
      ],
      color: 'orange'
    },
    {
      icon: Users,
      title: 'Copropriétaires',
      description: 'Gérez facilement vos coproprietaires, locataires et leurs informations.',
      details: [
        'Annuaire complet',
        'Import/Export CSV',
        'Sous-comptes délégués',
        'Espace coproprietaire dédié'
      ],
      color: 'pink'
    },
    {
      icon: BarChart3,
      title: 'Comptabilité',
      description: 'Module comptable complet conforme à la réglementation des copropriétés.',
      details: [
        'Budget prévisionnel',
        'Appels de fonds',
        'Suivi des impayés',
        'Rapprochement bancaire'
      ],
      color: 'cyan'
    },
    {
      icon: FileCheck,
      title: 'Carnet d\'Entretien',
      description: 'Conformité loi SRU avec un carnet d\'entretien numérique complet.',
      details: [
        'Fiche technique immeuble',
        'Suivi des équipements',
        'Contrôles obligatoires',
        'Export PDF réglementaire'
      ],
      color: 'amber'
    },
    {
      icon: MessageSquare,
      title: 'Messagerie',
      description: 'Communication simplifiée avec tous les acteurs de la copropriété.',
      details: [
        'Conversations par immeuble',
        'Notifications en temps réel',
        'Historique complet',
        'Pièces jointes'
      ],
      color: 'indigo'
    },
    {
      icon: Bell,
      title: 'Incidents',
      description: 'Signalement et suivi des incidents par les coproprietaires et gestionnaires.',
      details: [
        'Signalement avec photos',
        'Affectation prestataires',
        'Suivi des statuts',
        'Archivage automatique'
      ],
      color: 'red'
    },
    {
      icon: Wrench,
      title: 'Prestataires',
      description: 'Gérez votre réseau de prestataires et leurs interventions.',
      details: [
        'Carnet d\'adresses',
        'Catégories métiers',
        'Historique interventions',
        'Évaluations'
      ],
      color: 'slate'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="website-application">
      <WebsiteNavbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Une application complète pour votre syndic
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-10">
            Découvrez toutes les fonctionnalités qui font de SyndicAppli la solution préférée des syndics professionnels.
          </p>
          <Link
            to="/site/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:shadow-xl transition-all"
          >
            Demander une démo
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16">
            {mainFeatures.map((feature, index) => (
              <div
                key={index}
                data-testid={`app-feature-${index}`}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-${feature.color}-100 mb-6`}>
                    <feature.icon className={`w-8 h-8 text-${feature.color}-600`} />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feature.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6">
                    {feature.description}
                  </p>
                  <ul className="space-y-3">
                    {feature.details.map((detail, dIndex) => (
                      <li key={dIndex} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-slate-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className="flex-1">
                  <div className={`aspect-video rounded-2xl bg-gradient-to-br from-${feature.color}-100 to-${feature.color}-200 flex items-center justify-center`}>
                    <feature.icon className={`w-24 h-24 text-${feature.color}-400`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Prêt à découvrir SyndicAppli ?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Réservez une démonstration personnalisée avec notre équipe
          </p>
          <Link
            to="/site/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:shadow-xl transition-all"
          >
            Réserver ma démo gratuite
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export { WebsiteApplication };
